import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Container,
  Fade,
  Zoom,
  Avatar,
  useTheme,
  useMediaQuery
  // LinearProgress,
} from '@mui/material';
import {
  People,
  VpnKey,
  HealthAndSafety,
  Security,
  Assessment,
  Warning,
  CheckCircle,
  Refresh,
  // TrendingUp,
  Storage,
  AdminPanelSettings,
  Speed,
  Error as ErrorIcon,
  Token as TokenIcon
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LinearProgress from '@mui/material/LinearProgress';

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalApiKeys: number;
  activeApiKeys: number;
  totalPrompts: number;
  recentPrompts: number;
  systemHealth: {
    status: 'healthy' | 'warning' | 'error';
    database: boolean;
    redis: boolean;
    uptime: string;
  };
  recentActivity: Array<{
    type: string;
    message: string;
    timestamp: string;
  }>;
}

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usageStats, setUsageStats] = useState<{
    token_limit: number | null;
    tokens_used_this_month: number;
    tokens_remaining: number | null;
    usage_percent: number;
    quota_enabled: boolean;
    reset_at: string;
  } | null>(null);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      fetchAdminStats();
    }
  }, [user]);

  const fetchAdminStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsResponse, usageResponse] = await Promise.all([
        axios.get('/api/admin/system-stats'),
        axios.get('/api/auth/usage')
      ]);
      const systemStats = statsResponse.data;

      const adminStats: AdminStats = {
        totalUsers: systemStats.users?.total || 0,
        activeUsers: systemStats.users?.active || 0,
        totalApiKeys: systemStats.api_keys?.active || 0,
        activeApiKeys: systemStats.api_keys?.active || 0,
        totalPrompts: systemStats.prompts?.total || 0,
        recentPrompts: 0,
        systemHealth: {
          status: 'healthy',
          database: true,
          redis: true,
          uptime: 'Running'
        },
        recentActivity: [
          {
            type: 'info',
            message: 'System monitoring active',
            timestamp: new Date().toISOString()
          }
        ]
      };

      setStats(adminStats);
      setUsageStats(usageResponse.data);
    } catch (error: any) {
      console.error('Failed to fetch admin stats:', error);
      setError('Failed to load admin statistics');
      toast.error('Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  };

  const getHealthStatus = () => {
    if (!stats) return { color: 'default' as const, icon: <HealthAndSafety /> };

    switch (stats.systemHealth.status) {
      case 'healthy':
        return { color: 'success' as const, icon: <CheckCircle /> };
      case 'warning':
        return { color: 'warning' as const, icon: <Warning /> };
      case 'error':
        return { color: 'error' as const, icon: <Warning /> };
      default:
        return { color: 'default' as const, icon: <HealthAndSafety /> };
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            Access denied. You need admin privileges to access the admin panel.
          </Alert>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress size={60} />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Loading admin panel...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Alert
            severity="error"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={fetchAdminStats}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  const healthStatus = getHealthStatus();

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Enhanced Header with Gradient Background */}
        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: 4,
              mb: 4,
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
                pointerEvents: 'none'
              }
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <AdminPanelSettings sx={{ fontSize: 36 }} />
                </Avatar>
                <Box flex={1}>
                  <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" gutterBottom>
                    Admin Panel
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.95 }}>
                    Monitor system health, manage users, configure security rules, and oversee API
                    usage
                  </Typography>
                </Box>
                {!isMobile && (
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<Refresh />}
                    onClick={fetchAdminStats}
                    disabled={loading}
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    Refresh
                  </Button>
                )}
              </Box>
              {isMobile && (
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  startIcon={<Refresh />}
                  onClick={fetchAdminStats}
                  disabled={loading}
                  sx={{
                    mt: 2,
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Refresh
                </Button>
              )}
            </Box>
          </Paper>
        </Fade>
        {/* Enhanced Statistics Cards */}
        <Grid container spacing={3}>
          {/* User Statistics */}
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in timeout={600}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: 'primary.light',
                        color: 'primary.main'
                      }}
                    >
                      <People sx={{ fontSize: 32 }} />
                    </Avatar>
                  </Box>
                  <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                    {stats?.totalUsers || 0}
                  </Typography>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Total Users
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={2}>
                    <Chip
                      label={`${stats?.activeUsers || 0} active`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>

          {/* API Keys Statistics */}
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in timeout={700}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: 'secondary.light',
                        color: 'secondary.main'
                      }}
                    >
                      <VpnKey sx={{ fontSize: 32 }} />
                    </Avatar>
                  </Box>
                  <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                    {stats?.activeApiKeys || 0}
                  </Typography>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Active API Keys
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={2}>
                    <Chip
                      label={`${stats?.totalApiKeys || 0} total`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>

          {/* Prompts Statistics */}
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in timeout={800}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: 'info.light',
                        color: 'info.main'
                      }}
                    >
                      <Assessment sx={{ fontSize: 32 }} />
                    </Avatar>
                  </Box>
                  <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                    {stats?.totalPrompts || 0}
                  </Typography>
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    Total Prompts
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={2}>
                    <Chip label="Processed" size="small" color="info" variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>

          {/* System Health */}
          <Grid item xs={12} sm={6} md={3}>
            <Zoom in timeout={900}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: 'success.light',
                        color: 'success.main'
                      }}
                    >
                      <HealthAndSafety sx={{ fontSize: 32 }} />
                    </Avatar>
                  </Box>
                  <Chip
                    icon={healthStatus.icon}
                    label={stats?.systemHealth.status.toUpperCase() || 'UNKNOWN'}
                    color={healthStatus.color}
                    sx={{ mb: 2, fontWeight: 'bold' }}
                  />
                  <Typography color="textSecondary" variant="body2" gutterBottom>
                    System Health
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {stats?.systemHealth.uptime || 'Unknown'}
                  </Typography>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>

          {/* Token Usage */}
          {usageStats && (
            <Grid item xs={12} sm={6} md={3}>
              <Zoom in timeout={1000}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 }
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: 'warning.light',
                          color: 'warning.main'
                        }}
                      >
                        <TokenIcon sx={{ fontSize: 32 }} />
                      </Avatar>
                    </Box>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                      {(usageStats.tokens_used_this_month ?? 0).toLocaleString()}
                    </Typography>
                    <Typography color="textSecondary" variant="body2" gutterBottom>
                      Tokens Used This Month
                    </Typography>
                    {usageStats.quota_enabled ? (
                      <>
                        <LinearProgress
                          variant="determinate"
                          value={usageStats.usage_percent}
                          color={
                            usageStats.usage_percent >= 90
                              ? 'error'
                              : usageStats.usage_percent >= 70
                                ? 'warning'
                                : 'success'
                          }
                          sx={{ borderRadius: 1, height: 6, my: 1 }}
                        />
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="caption" color="textSecondary">
                            {usageStats.usage_percent}% of{' '}
                            {usageStats.token_limit!.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {usageStats.tokens_remaining!.toLocaleString()} left
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Chip
                        label="No quota set"
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          )}

          {/* Enhanced System Components Status */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={1000}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                    <Storage />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold">
                    System Components
                  </Typography>
                </Box>
                <List dense>
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: stats?.systemHealth.database ? 'success.light' : 'error.light',
                      color: stats?.systemHealth.database ? 'success.dark' : 'error.dark'
                    }}
                  >
                    <ListItemIcon>
                      {stats?.systemHealth.database ? (
                        <CheckCircle color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          Database Connection
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption">
                          {stats?.systemHealth.database ? 'Connected' : 'Disconnected'}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: stats?.systemHealth.redis ? 'success.light' : 'error.light',
                      color: stats?.systemHealth.redis ? 'success.dark' : 'error.dark'
                    }}
                  >
                    <ListItemIcon>
                      {stats?.systemHealth.redis ? (
                        <CheckCircle color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          Redis Cache
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption">
                          {stats?.systemHealth.redis ? 'Connected' : 'Disconnected'}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem
                    sx={{
                      borderRadius: 2,
                      bgcolor: 'success.light',
                      color: 'success.dark'
                    }}
                  >
                    <ListItemIcon>
                      <Security color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          Security Services
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption">All security checks active</Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Paper>
            </Fade>
          </Grid>

          {/* Enhanced Quick Actions */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={1100}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.main' }}>
                    <Speed />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold">
                    Quick Actions
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<People />}
                      onClick={() => navigate('/users')}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'primary.light',
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      Users
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<VpnKey />}
                      onClick={() => navigate('/api-keys')}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'secondary.light',
                          borderColor: 'secondary.main'
                        }
                      }}
                    >
                      API Keys
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Security />}
                      onClick={() => navigate('/security-rules')}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'warning.light',
                          borderColor: 'warning.main'
                        }
                      }}
                    >
                      Security
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Assessment />}
                      onClick={() => navigate('/prompt-history')}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'info.light',
                          borderColor: 'info.main'
                        }
                      }}
                    >
                      Prompts
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Fade>
          </Grid>

          {/* Enhanced System Information */}
          <Grid item xs={12}>
            <Fade in timeout={1200}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  background:
                    'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <Avatar sx={{ bgcolor: 'info.light', color: 'info.main' }}>
                    <AdminPanelSettings />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold">
                    System Administration
                  </Typography>
                </Box>
                <Typography color="textSecondary" paragraph>
                  Welcome to the GuardLLM Admin Panel. Here you can monitor system health, manage
                  users, configure security rules, and oversee API usage.
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        gutterBottom
                        display="block"
                      >
                        Current User
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {user?.email}
                      </Typography>
                      <Chip label={user?.role} size="small" color="primary" sx={{ mt: 1 }} />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        gutterBottom
                        display="block"
                      >
                        System Version
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        GuardLLM v1.0.0
                      </Typography>
                      <Chip label="Latest" size="small" color="success" sx={{ mt: 1 }} />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        gutterBottom
                        display="block"
                      >
                        Last Updated
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {new Date().toLocaleDateString()}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ mt: 1, display: 'block' }}
                      >
                        {new Date().toLocaleTimeString()}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        gutterBottom
                        display="block"
                      >
                        Environment
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        Development
                      </Typography>
                      <Chip label="Active" size="small" color="warning" sx={{ mt: 1 }} />
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Fade>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default AdminPanel;
